import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import type { ImportDiffDto, ImportItem, InvoiceId } from "../api/types";
import {
  useConfig,
  useImportRerun,
  useImports,
  useUpdateImport,
} from "../api/queries";
import { ImportActions } from "../pages/Admin/ImportsPage/components/ImportActions";
import { getImportWarnings } from "../pages/Admin/ImportsPage/warnings";
import { DialogTitleWithClose } from "./DialogTitleWithClose";
import { InvoicePickerField } from "./EntityPickerField";
import { ImportDiffModal } from "./ImportDiffModal";
import { ImportTransactionsModal } from "./ImportTransactionsModal";
import { ImportCsvModal } from "./ImportCsvModal";
import { ConfirmDialog } from "./ConfirmDialog";

export interface ImportDetailsModalProps {
  open: boolean;
  onClose: () => void;
  importItem: ImportItem | null;
}

/**
 * Self-contained "Import details" view: the read-only metadata + invoice editor
 * plus the full action set from the imports list (re-run, review/upload pending
 * version, view transactions, view CSV, delete/restore) and the modals those
 * open. Because it owns everything, both the Imports page (clicking a name) and
 * the dashboard's needs-re-run warning can drive an import to a fixed state
 * without leaving the page. Mount it with a `key={item.id}` so switching imports
 * resets local state.
 */
export const ImportDetailsModal = ({
  open,
  onClose,
  importItem,
}: ImportDetailsModalProps) => {
  const updateImport = useUpdateImport();
  const importRerun = useImportRerun();
  const { data: allImports } = useImports();
  const { data: config } = useConfig();

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<InvoiceId | null>(
    importItem?.invoiceId ?? null,
  );
  const [diffOpen, setDiffOpen] = useState(false);
  const [preloadedDiff, setPreloadedDiff] = useState<ImportDiffDto | null>(
    null,
  );
  const [txOpen, setTxOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read the freshest row from the imports cache so re-run / apply / delete
  // performed inside this modal reflect immediately (the list invalidation
  // refetches this query), rather than showing the snapshot we were opened with.
  const currentItem =
    allImports?.find((i) => i.id === importItem?.id) ?? importItem;

  const usedInvoiceIds = useMemo(() => {
    const set = new Set<InvoiceId>();
    for (const i of allImports ?? []) {
      if (i.invoiceId && i.id !== importItem?.id) set.add(i.invoiceId);
    }
    return set;
  }, [allImports, importItem?.id]);

  const showInvoicePicker = useMemo(() => {
    if (!currentItem || !config) return true;
    const importerConfig = config.importConfigs.find(
      (c) => c.name === currentItem.importerName,
    );
    return importerConfig?.hasInvoice ?? true;
  }, [config, currentItem]);

  if (!importItem || !currentItem) return null;

  const warnings = getImportWarnings(currentItem);

  // The invoice is the only editable field, so "changed" means the picked
  // invoice differs from what's persisted. When the picker is hidden there is
  // nothing to save.
  const isDirty =
    showInvoicePicker && selectedInvoiceId !== (currentItem.invoiceId ?? null);

  const transactionRange =
    currentItem.firstTransactionDate && currentItem.lastTransactionDate
      ? `${currentItem.firstTransactionDate} – ${currentItem.lastTransactionDate}`
      : "—";

  const handleSave = () => {
    updateImport.mutate(
      {
        importId: currentItem.id,
        body: { invoiceId: showInvoicePicker ? selectedInvoiceId : null },
      },
      {
        onSuccess: () => onClose(),
        onError: () => setError("Failed to update import."),
      },
    );
  };

  const handleRerun = () => {
    setError(null);
    importRerun.mutate(currentItem.id, {
      onSuccess: (diff) => {
        setPreloadedDiff(diff);
        setDiffOpen(true);
      },
      onError: (e) =>
        setError(
          `Re-run failed: ${e instanceof Error ? e.message : String(e)}`,
        ),
    });
  };

  const handleRestore = () => {
    updateImport.mutate(
      { importId: currentItem.id, body: { deleted: false } },
      { onError: () => setError("Failed to restore import.") },
    );
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    updateImport.mutate(
      { importId: currentItem.id, body: { deleted: true } },
      {
        onSuccess: () => onClose(),
        onError: () => setError("Failed to delete import."),
      },
    );
  };

  const closeDiff = () => {
    setDiffOpen(false);
    setPreloadedDiff(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithClose onClose={onClose}>
          Import details
        </DialogTitleWithClose>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ImportActions
                item={currentItem}
                onViewTransactions={() => setTxOpen(true)}
                onViewCsv={() => setCsvOpen(true)}
                onOpenDiff={() => {
                  setPreloadedDiff(null);
                  setDiffOpen(true);
                }}
                onRerun={handleRerun}
                isRerunning={
                  importRerun.isPending &&
                  importRerun.variables === currentItem.id
                }
                onDelete={() => setConfirmDeleteOpen(true)}
                onRestore={handleRestore}
              />
            </Box>

            {warnings.length > 0 && (
              <Alert severity="warning">
                <Stack spacing={0.25}>
                  {warnings.map((w) => (
                    <span key={w.code}>{w.message}</span>
                  ))}
                </Stack>
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Import ID"
              value={currentItem.id}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label="Date Imported"
              value={currentItem.dateImported}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label="Transaction Date Range"
              value={transactionRange}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label="Importer Name"
              value={currentItem.importerName}
              disabled
              fullWidth
              size="small"
            />

            {showInvoicePicker && (
              <InvoicePickerField
                accountInvoicesOnly
                value={selectedInvoiceId}
                onChange={setSelectedInvoiceId}
                size="small"
                targetRange={{
                  start: currentItem.firstTransactionDate ?? null,
                  end: currentItem.lastTransactionDate ?? null,
                }}
                usedInvoiceIds={usedInvoiceIds}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!isDirty}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {diffOpen && (
        <ImportDiffModal
          open
          onClose={closeDiff}
          importId={currentItem.id}
          preloadedDiff={preloadedDiff}
        />
      )}
      {txOpen && (
        <ImportTransactionsModal
          importItem={currentItem}
          onClose={() => setTxOpen(false)}
        />
      )}
      <ImportCsvModal
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        importItem={currentItem}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete import?"
        message={`The import "${currentItem.importerName}" and its transactions will be hidden. You can restore it later from the Deleted view.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </>
  );
};
